import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
import { Menu, MenuHandler, MenuList } from '@material-tailwind/react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PiCaretDownLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import {
  getMenuItemClasses,
  menuListClasses,
} from 'src/modules/shared/styles/dropdown.styles';
import type { QuickSelectionMenuProps } from 'src/modules/shared/types/quick-selection-menu.type';

/**
 * Present independent options behind one compact, accessible icon button.
 *
 * Uses shared button/menu styles and the menu primitive's keyboard navigation,
 * focus restoration, outside/Escape dismissal, and viewport collision handling.
 *
 * @param props - Generic options, accessible name, and standard button props.
 * @returns An icon trigger and an anchored menu, with no domain-specific behavior.
 */
export const QuickSelectionMenu: React.FC<QuickSelectionMenuProps> = ({
  label,
  showTooltip = true,
  shape,
  caption,
  items,
  icon = <PiCaretDownLight className="h-4 w-4" />,
  placement = 'bottom-end',
  size = 'md',
  variant = 'neutral',
  appearance = 'ghost',
  className = '',
  disabled = false,
  loading = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const setOpen = useCallback(
    (value: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlledOpen, onOpenChange]
  );
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);
  const captionId = useId();
  const unavailable =
    disabled || loading || !items.some((item) => !item.disabled);

  const closeLayerId = useCloseRequest({
    isOpen: open && !unavailable,
    onClose: () => setOpen(false),
  });

  useEffect(() => {
    if (unavailable) setOpen(false);
  }, [unavailable, setOpen]);

  return (
    <CloseRequestContext.Provider value={closeLayerId}>
      <Tooltip
        content={showTooltip ? label : undefined}
        size="xs"
        as="span"
        enabled={!open}
      >
        <Menu
          open={open && !unavailable}
          handler={(nextOpen: boolean) => {
            if (!nextOpen || !unavailable) setOpen(nextOpen);
          }}
          placement={placement}
          offset={6}
        >
          <MenuHandler>
            <Button
              ref={triggerRef}
              type="button"
              className={className}
              size={size}
              variant={variant}
              appearance={appearance}
              shape={shape}
              disabled={unavailable}
              loading={loading}
              isIconOnly
              aria-label={label}
              aria-haspopup="menu"
            >
              {icon}
            </Button>
          </MenuHandler>
          <MenuList
            ref={setMenuElement}
            aria-label={label}
            aria-describedby={caption ? captionId : undefined}
            className={`${menuListClasses} min-w-0 w-[210px] max-w-[calc(100vw-2rem)] gap-0 rounded-[9px] border-slate-200 bg-white p-[5px] text-slate-900 shadow-lg`}
          >
            {items.map((item) => (
              <Button
                key={item.key}
                type="button"
                size={size}
                variant="neutral"
                appearance="ghost"
                className={`${getMenuItemClasses(size, false, false, item.disabled)} !h-auto min-h-[32px] !justify-between !p-2 !text-slate-900`}
                disabled={unavailable || item.disabled}
                aria-label={item.ariaLabel ?? item.label}
                onClick={() => {
                  setOpen(false);
                  triggerRef.current?.focus();
                  item.onClick();
                }}
              >
                <Text
                  as="span"
                  size="small"
                  className="min-w-0 whitespace-normal text-left !text-[13px] leading-[1.5]"
                >
                  {item.label}
                </Text>
                {item.icon ? (
                  <span aria-hidden="true" className="inline-flex shrink-0">
                    {item.icon}
                  </span>
                ) : null}
              </Button>
            ))}
          </MenuList>
          {/* MenuList registers every direct child as an option. Portaling the
            caption keeps it in the surface without adding a keyboard stop. */}
          {caption && menuElement ? (
            <>
              {createPortal(
                <div id={captionId} role="presentation" className="order-first">
                  <Text
                    as="div"
                    size="tiny"
                    className="px-2 pb-[5px] pt-[3px] leading-[1.5] text-slate-500"
                  >
                    {caption}
                  </Text>
                </div>,
                menuElement
              )}
            </>
          ) : null}
        </Menu>
      </Tooltip>
    </CloseRequestContext.Provider>
  );
};
